import { useState } from "react";
import { useStore } from "../store";
import { parseSpecFile, parseSpecFromURL } from "../api";
import { searchIndex } from "../search";

export function FileLoader() {
  const [isDragging, setIsDragging] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [url, setUrl] = useState("");
  const [error, setError] = useState<string | null>(null);
  
  const addSpec = useStore((state) => state.addSpec);
  const endpoints = useStore((state) => state.endpoints);

  const handleFileSelect = async (files: FileList | null) => {
    if (!files || files.length === 0) return;

    setIsLoading(true);
    setError(null);

    try {
      const newSpecs = [];
      for (const file of Array.from(files)) {
        if (!file.name.endsWith(".json") && !file.name.endsWith(".yaml") && !file.name.endsWith(".yml")) {
          continue;
        }
        const spec = await parseSpecFile(file);
        newSpecs.push(spec);
        addSpec(spec);
      }

      // Rebuild search index with all endpoints
      searchIndex.buildIndex([...endpoints, ...newSpecs.flatMap((s) => s.endpoints)]);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to parse OpenAPI spec");
    } finally {
      setIsLoading(false);
      setIsDragging(false);
    }
  };

  const handleUrlLoad = async () => {
    if (!url.trim()) return;

    setIsLoading(true);
    setError(null);

    try {
      const spec = await parseSpecFromURL(url);
      addSpec(spec);
      searchIndex.buildIndex([...endpoints, ...spec.endpoints]);
      setUrl("");
      setShowUrlInput(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load OpenAPI spec from URL");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    handleFileSelect(e.dataTransfer.files);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  return (
    <div className="file-loader">
      <div
        className={`drop-zone ${isDragging ? "dragging" : ""}`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
      >
        <input
          type="file"
          accept=".json,.yaml,.yml"
          multiple
          onChange={(e) => handleFileSelect(e.target.files)}
          style={{ display: "none" }}
          id="file-input"
        />
        <label htmlFor="file-input" style={{ cursor: "pointer", width: "100%", textAlign: "center" }}>
          {isLoading ? (
            <div className="loader">
              <div className="spinner" />
              <span>Loading...</span>
            </div>
          ) : (
            <>
              <svg className="upload-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="14 2 14 8 20 8" />
                <polyline points="17 8 12 3 7 8" />
                <line x1="12" y1="3" x2="12" y2="15" />
              </svg>
              <p className="drop-text">
                <strong>Drop OpenAPI specs here</strong> or click to browse
              </p>
              <p className="drop-hint">Supports JSON, YAML, and YML files</p>
            </>
          )}
        </label>
      </div>

      <div className="url-loader">
        {!showUrlInput ? (
          <button className="url-btn" onClick={() => setShowUrlInput(true)}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <line x1="2" y1="12" x2="22" y2="12" />
              <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
            </svg>
            Load from URL
          </button>
        ) : (
          <div className="url-input-group">
            <input
              type="url"
              placeholder="https://api.example.com/openapi.json"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleUrlLoad()}
              autoFocus
            />
            <button onClick={handleUrlLoad} disabled={isLoading}>
              Load
            </button>
            <button onClick={() => setShowUrlInput(false)} className="cancel">
              Cancel
            </button>
          </div>
        )}
      </div>

      {error && (
        <div className="error-message">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          {error}
        </div>
      )}
    </div>
  );
}
